import {
  Box,
  Flex,
  HStack,
  Heading,
  IconButton,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import {
  DataTypeConfigUtils,
  ManualContentHelpers,
  ObjectContentNode,
  QueryNodeUtils,
  RuntimeOperations,
} from '@helo/cms'
import { List, flow, pipe } from '@helo/utils'
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise'
import { useEffect, useMemo, useState } from 'react'

import { ExportModal } from '../../components/ExportModal/ExportModal'
import { Icon } from '../../components/Icons/Icon'
import { PageLayout } from '../../components/PageLayout/PageLayout'
import { DeleteModal } from '../../components/SavedViewsTable/SavedViewsModal'
import { Table } from '../../components/Table'
import { showToast } from '../../components/Toast/showToast'
import { savedViewService } from '../../services/savedViewService'
import { useAppStore } from '../../store/appStore/appStore'
import { browserTabStaticPageTitles } from '../../store/appStore/browserDocumentTitle/browserTabTitleSpec'
import { SavedView } from '../../store/appStore/view/viewSlice.types'
import {
  SavedViewWithLabel,
  handleShare,
  prepareViews,
} from './SavedViewsPage.parts'

export const SavedViewsPage = () => {
  const [savedViews, setSavedViews] = useState<SavedViewWithLabel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const loadViews = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const views = await savedViewService.fetchViews()
        const preparedViews = pipe(views, prepareViews)
        setSavedViews(preparedViews)
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to load saved views'),
        )
        showToast({
          title: 'Error Loading Views',
          description: 'Failed to load saved views. Please try again.',
          variant: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadViews()
  }, [])

  const savedViewType = useAppStore(state =>
    pipe(
      state.cms.baseDataTypeConfigs,
      DataTypeConfigUtils.find([['typeName', 'SavedView']]),
    ),
  )
  const routes = useAppStore(state => state.router.routes)

  if (!savedViewType) {
    throw new Error('Saved View Type not found')
  }

  const viewsAsContentNodes = useMemo(
    () =>
      pipe(
        savedViews,
        List.map(
          flow(
            view => ManualContentHelpers.createContentNode(savedViewType, view),
            RuntimeOperations.processContentNode(),
            processedNode => {
              // Override link state on field + ofType; table cells merge both and lazy CMS
              // state can otherwise keep a stale STRING_BUILDER url from savedViewStateConfig.
              if (processedNode.kind !== 'OBJECT') return processedNode

              const view = savedViews.find(
                v => v.id === (processedNode.data as SavedViewWithLabel)?.id,
              )
              if (!view) return processedNode

              const route = useAppStore
                .getState()
                .router.getRouteById(view.viewId)

              if (route && view.id) {
                const linkState = {
                  linkUrl: route.path,
                  linkUrlStorage: {
                    savedViewId: view.id,
                  },
                }
                const nameField = processedNode.fields?.find(
                  f => f.key === 'name',
                )
                if (nameField) {
                  nameField.state = {
                    ...nameField.state,
                    ...linkState,
                  }
                  if (nameField.ofType) {
                    nameField.ofType.state = {
                      ...nameField.ofType.state,
                      ...linkState,
                    }
                  }
                }
              }

              return processedNode
            },
          ),
        ),
      ),
    [savedViews, savedViewType, routes],
  )

  // Create column definitions with tableColumnConfig
  const savedViewColumnDefsWithConfig = useMemo(() => {
    if (savedViewType.kind !== 'OBJECT') {
      return savedViewColumnDefs
    }

    return savedViewColumnDefs.map(colDef => {
      // Skip columns that already have a custom cellRenderer (like actions column)
      // They don't need tableColumnConfig since they use their own renderer
      if (colDef.cellRenderer && typeof colDef.cellRenderer === 'function') {
        return colDef
      }

      // Skip columns without a field
      if (!colDef.field) {
        return colDef
      }

      // Find the field config for this column
      const fieldConfig = DataTypeConfigUtils.find([['key', colDef.field]])(
        savedViewType,
      )

      if (!fieldConfig) {
        return colDef
      }

      // Create tableColumnConfig
      const tableColumnConfig = {
        key: colDef.field,
        selector: [['key', colDef.field]] as any,
        config: fieldConfig,
        columnConfig: {
          key: colDef.field,
        },
      }

      return {
        ...colDef,
        // Move tableColumnConfig to context as AG Grid suggests for application data
        context: {
          ...(colDef.context ?? {}),
          tableColumnConfig,
        },
      }
    })
  }, [savedViewType])

  const hasSavedViews = savedViews?.length > 0

  const context = useMemo(() => {
    return {
      handleDelete: async (savedView: SavedView & { id?: string }) => {
        try {
          if (savedView.id) {
            await savedViewService.deleteView(savedView.id)
          } else {
            await savedViewService.deleteViewByName(savedView.name)
          }

          // Reload views after deletion
          const views = await savedViewService.fetchViews()
          const preparedViews = pipe(views, prepareViews)
          setSavedViews(preparedViews)

          showToast({
            title: 'View Deleted',
            description: `"${savedView.name}" was successfully deleted!`,
          })
        } catch (err) {
          showToast({
            title: 'Error Deleting View',
            description: 'Failed to delete view. Please try again.',
            variant: 'error',
          })
        }
      },
    }
  }, [])

  return (
    <PageLayout
      documentTitle={browserTabStaticPageTitles.savedViews}
      headerSlot={
        <Flex alignItems="center" gap="8px">
          <Icon icon="SaveIcon" boxSize="26px" color="presets.neutral.90" />
          <Heading variant="pageTitle">Saved Views</Heading>
        </Flex>
      }
    >
      <VStack alignItems="start" flex="1 1 0">
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" flex="1 1 0">
            <Spinner size="xl" />
          </Flex>
        ) : error ? (
          <Text fontSize="16px" color="presets.red.55">
            Error loading saved views: {error.message}
          </Text>
        ) : (
          <>
            {!hasSavedViews && (
              <Text fontSize="16px" fontWeight={500}>
                To save a new view, navigate to any of the{' '}
                <strong>View My</strong> list views and begin customizing the
                view.
              </Text>
            )}
            <Text
              fontSize="12px"
              fontWeight={600}
              padding="23px 0 0 0"
              alignContent="center"
            >
              {savedViews?.length} records
            </Text>
            <Box flex="1 1 0" width="100%">
              <Table
                rowData={viewsAsContentNodes}
                context={context}
                columnDefs={savedViewColumnDefsWithConfig}
                style={{ height: '100%' }}
              />
            </Box>
          </>
        )}
      </VStack>
    </PageLayout>
  )
}

const savedViewColumnDefs: ColDef[] = [
  {
    field: 'name',
    headerName: 'View Name',

    autoHeight: true,
    wrapText: true,
    flex: 1,
    suppressAutoSize: true,
    suppressSizeToFit: true,
  },
  {
    field: 'description',
    headerName: 'Description',
    autoHeight: true,
    wrapText: true,
    maxWidth: 400,
  },
  {
    field: 'viewId',
    headerName: 'Parent View',
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: function ParentViewCellRenderer({
      data,
    }: ICellRendererParams<ObjectContentNode, ObjectContentNode>) {
      if (!data) return null
      const savedView = data.data as SavedViewWithLabel
      const route = useAppStore.getState().router.getRouteById(savedView.viewId)
      return route?.label ?? savedView.viewId
    },
  },
  {
    field: 'createdDate',
    headerName: 'Created',
    suppressAutoSize: true,
    suppressSizeToFit: true,
  },
  {
    field: 'updatedDate',
    headerName: 'Last Updated',
    suppressAutoSize: true,
    suppressSizeToFit: true,
  },
  {
    // suppressAutoSize: true,
    // suppressSizeToFit: true,
    // Actions
    cellRenderer: function ActionsComponent({
      context,
      data,
    }: ICellRendererParams<ObjectContentNode, ObjectContentNode>) {
      const [isModalOpen, setIsModalOpen] = useState(false)
      const { isOpen, onOpen, onClose } = useDisclosure()
      const savedView = data!.data as SavedView & { tableConfig?: any }
      const {
        viewState: { queryStates },
        name,
        tableConfig,
      } = savedView

      const columns = tableConfig?.columns
      const columnConfigs = tableConfig?.columnConfigs
      const connectionLocator = tableConfig?.connectionLocator

      const queryNode = tableConfig?.queryId
        ? useAppStore.getState().queryStore.getQuery(tableConfig.queryId)
        : undefined

      const queryDefinitionId =
        queryNode?.queryDefinition?.id ?? tableConfig?.queryId

      const queryVariables = queryNode
        ? pipe(queryNode, QueryNodeUtils.getMappedVariablesAsObject())
        : (() => {
            // Fallback to variables from saved view's queryStates
            const matchingQueryState = queryStates.find(
              qs => qs.queryId === tableConfig?.queryId,
            )
            return matchingQueryState?.variables || {}
          })()

      const canExport = Boolean(queryDefinitionId)

      return (
        <HStack justify="right">
          {tableConfig && (
            <ExportModal
              queryId={tableConfig.queryId}
              title={name}
              exportTitle={name}
              isOpen={isOpen}
              onOpen={canExport ? onOpen : undefined}
              onClose={onClose}
              queryVariables={queryVariables}
              queryDefinitionId={queryDefinitionId}
              columns={columns}
              columnConfigs={columnConfigs}
              connectionLocator={connectionLocator}
              isDisabled={!canExport}
              renderTrigger={
                <IconButton
                  variant="ghost"
                  color="presets.turquoise.50"
                  aria-label="Export"
                  icon={<Icon icon="DownloadIcon" boxSize="20px" />}
                  onClick={onOpen}
                />
              }
            />
          )}
          <IconButton
            onClick={() => {
              handleShare(data!)
            }}
            aria-label="share"
            variant="ghost"
            color="presets.turquoise.50"
            icon={<Icon icon="ShareIcon" boxSize="20px" />}
          ></IconButton>
          <IconButton
            aria-label="delete"
            variant="ghost"
            onClick={() => {
              setIsModalOpen(true)
            }}
            icon={
              <Icon icon="TrashIcon" color="presets.red.55" boxSize="20px" />
            }
          ></IconButton>
          <DeleteModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={() => {
              setIsModalOpen(false)
              context.handleDelete(data!.data as SavedViewWithLabel)
            }}
          />
        </HStack>
      )
    },
  },
]
